import { ActionIcon, Box } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import * as React from "react";
import { ImageDescription } from "./Admin/Blog/BlogEditor/Extensions/Image/ImageDescription";
import css from "./Figure.module.css";
import { useLightBoxExtension } from "./Lightbox/LightBoxContext";
import { LightBoxHeader } from "./Lightbox/LightBoxHeader";

type FigureProps = Partial<{
  alt: string;
  src: string;
  width: string;
  height: string;
  title: string;
}>;

const Lightbox = dynamic(() => import("react-spring-lightbox"), {
  ssr: false,
});

export function Figure(image: FigureProps) {
  const [isLightboxOpened, setIsLightboxOpened] = React.useState(false);

  const { alt, height, src, title, width } = image;

  const onClose = () => {
    setIsLightboxOpened(false);
  };

  const openLightBox = () => {
    setIsLightboxOpened(true);
  };

  const lightboxImage = React.useMemo(
    () => ({
      alt,
      description: alt,
      title,
      src,
    }),

    [alt, src, title]
  );

  const { addImage, getImages, removeImage } = useLightBoxExtension();

  React.useEffect(() => {
    addImage(lightboxImage);

    return () => {
      removeImage(lightboxImage);
    };
  }, [addImage, lightboxImage, removeImage]);

  const imageArray = getImages();

  const [index, setIndex] = React.useState(getIndex(imageArray, lightboxImage));

  React.useEffect(() => {
    setIndex(getIndex(imageArray, lightboxImage));
  }, [imageArray, lightboxImage, src]);

  const gotoPrevious = () => {
    return index > 0 && setIndex(index - 1);
  };

  const gotoNext = () => {
    setIndex((currentIndex) => {
      if (currentIndex + 1 < imageArray.length) {
        return currentIndex + 1;
      }

      return currentIndex;
    });
  };

  return (
    <>
      <Box pos="relative" component="figure" mb="20px" display="inline-block">
        {src && (
          <Image
            onClick={openLightBox}
            src={src}
            style={{ maxWidth: "90vw", height: "100%", cursor: "pointer" }}
            width={Math.round(Number(width))}
            height={Math.round(Number(height))}
            title={title}
            alt={alt ?? ""}
          />
        )}
        {alt && <ImageDescription>{alt}</ImageDescription>}
      </Box>

      {isLightboxOpened && (
        <Lightbox
          onNext={gotoNext}
          onPrev={gotoPrevious}
          isOpen={true}
          images={imageArray.map((item) => ({ ...item, loading: "lazy" }))}
          style={{ background: "rgb(0 0 0 / 95%)" }}
          onClose={onClose}
          renderHeader={() => (
            <LightBoxHeader
              onClose={onClose}
              images={imageArray}
              currentIndex={index}
            />
          )}
          renderNextButton={({ canNext }) =>
            canNext && (
              <ActionIcon
                onClick={gotoNext}
                variant="transparent"
                size="3rem"
                className={css.button}
              >
                <IconChevronRight size="3rem" />
              </ActionIcon>
            )
          }
          renderPrevButton={({ canPrev }) =>
            canPrev && (
              <ActionIcon
                onClick={gotoPrevious}
                variant="transparent"
                size="3rem"
                className={css.button}
              >
                <IconChevronLeft size="3rem" />
              </ActionIcon>
            )
          }
          singleClickToZoom={true}
          currentIndex={index}
        />
      )}
    </>
  );
}

const getIndex = (
  images: { src?: string; title?: string; description?: string }[],
  searchImage: { src?: string; title?: string; description?: string }
) => {
  const index = images.findIndex((item) => item === searchImage);

  return index === -1 ? 0 : index;
};
